/**
 * API конфигурация для LX FBC
 * Автоматически определяет правильные URLs для разработки и продакшна
 */

// Версия для отладки
const APP_VERSION = "1.0.1-user-data";
console.log(`🚀 LX FBC App Version: ${APP_VERSION}`);

// Определяем окружение
// Явная проверка: если MODE === 'production' или PROD === true, то production
const isDevelopment = 
  import.meta.env.MODE === 'development' || 
  (import.meta.env.DEV && import.meta.env.MODE !== 'production');
const isProduction = 
  import.meta.env.MODE === 'production' || 
  import.meta.env.PROD;

// Конфигурация для разных окружений
const config = {
  development: {
    // Локальные серверы для разработки
    frontendUrl: "http://localhost:3132", // Vite dev server
    apiBaseUrl: "http://localhost:3131", // Local functions server
    functionsUrl: "http://localhost:3131", // Local functions server
  },
  production: {
    // Продакшн через единый домен с API Gateway (unified)
    frontendUrl: "https://lx.fbc.ru",
    apiBaseUrl: "https://lx.fbc.ru/api",
    functionsUrl: "https://lx.fbc.ru/api",
  },
};

// Выбираем конфигурацию на основе окружения
const currentConfig = isDevelopment ? config.development : config.production;

// Отладочная информация (только для development)
if (isDevelopment) {
  console.log('[API Config] Environment:', {
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
    isDevelopment,
    isProduction,
    selectedConfig: isDevelopment ? 'development' : 'production'
  });
}

// Экспортируем URLs
export const FRONTEND_URL = currentConfig.frontendUrl;
export const API_BASE_URL = currentConfig.apiBaseUrl;
export const FUNCTIONS_URL = currentConfig.functionsUrl;

// Auth API endpoints
export const AUTH_API_URL = `${API_BASE_URL}/auth`;

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH_INFO: `${AUTH_API_URL}`,
  LOGIN: `${AUTH_API_URL}/login`,
  REGISTER: `${AUTH_API_URL}/register`,
  FORGOT_PASSWORD: `${AUTH_API_URL}/forgot-password`,
  RESET_PASSWORD: `${AUTH_API_URL}/reset-password`,
  REFRESH_TOKEN: `${AUTH_API_URL}/refresh-token`,
  VERIFY_TOKEN: `${AUTH_API_URL}/verify-token`,
  LOGOUT: `${AUTH_API_URL}/logout`,
  PROFILE: `${AUTH_API_URL}/profile`,

  // Health checks
  HEALTH: isDevelopment
    ? "http://localhost:3131/health"
    : `${API_BASE_URL}/health`,
};

// HTTP клиент с автоматической конфигурацией
class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  // Получить заголовки с токеном авторизации
  getHeaders(additionalHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...additionalHeaders };

    // Добавляем токен авторизации если есть
    const token = this.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  // Базовый метод для выполнения запросов
  async request(url, options = {}) {
    const fullUrl = url.startsWith("http") ? url : `${this.baseURL}${url}`;

    const requestOptions = {
      ...options,
      headers: this.getHeaders(options.headers),
    };

    try {
      console.log(`🔄 API Request: ${options.method || "GET"} ${fullUrl}`);

      const response = await fetch(fullUrl, requestOptions);

      console.log(`📡 API Response: ${response.status} ${response.statusText}`);

      // Проверяем статус ответа
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));

        throw new APIError(
          errorData.message || "Request failed",
          response.status,
          errorData
        );
      }

      // Пытаемся парсить JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error("❌ API Error:", error);

      // Обрабатываем ошибки авторизации с попыткой обновить токен
      if (error.status === 401 && !options._isRetry) {
        console.log("🔄 Access token expired, attempting refresh...");
        
        try {
          // Пытаемся обновить токен
          await this.refreshAccessToken();
          
          // Повторяем запрос с новым токеном
          console.log("✅ Token refreshed, retrying request...");
          return await this.request(url, { ...options, _isRetry: true });
        } catch (refreshError) {
          console.error("❌ Token refresh failed:", refreshError);
          this.handleUnauthorized();
          throw refreshError;
        }
      }

      throw error;
    }
  }

  // HTTP методы
  get(url, options = {}) {
    return this.request(url, { ...options, method: "GET" });
  }

  post(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put(url, data, options = {}) {
    return this.request(url, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  delete(url, options = {}) {
    return this.request(url, { ...options, method: "DELETE" });
  }

  // Управление токенами
  getAccessToken() {
    // Проверяем оба варианта ключей для совместимости
    return localStorage.getItem("lx_access_token") || localStorage.getItem("authToken");
  }

  getRefreshToken() {
    // Проверяем оба варианта ключей для совместимости
    return localStorage.getItem("lx_refresh_token") || localStorage.getItem("refreshToken");
  }

  setTokens(tokens) {
    if (tokens.access_token) {
      localStorage.setItem("lx_access_token", tokens.access_token);
      localStorage.setItem("authToken", tokens.access_token); // Для совместимости
    }
    if (tokens.refresh_token) {
      localStorage.setItem("lx_refresh_token", tokens.refresh_token);
      localStorage.setItem("refreshToken", tokens.refresh_token); // Для совместимости
    }
    if (tokens.id_token) {
      localStorage.setItem("lx_id_token", tokens.id_token);
    }
    
    // Сохраняем tokenExpiry для setupTokenRefresh
    if (tokens.expires_in) {
      const expiryTime = new Date().getTime() + tokens.expires_in * 1000;
      localStorage.setItem("tokenExpiry", expiryTime.toString());
      console.log(`⏱️  Token expires in ${tokens.expires_in}s (at ${new Date(expiryTime).toLocaleTimeString()})`);
    }
  }

  clearTokens() {
    // Очищаем оба варианта ключей
    localStorage.removeItem("lx_access_token");
    localStorage.removeItem("lx_refresh_token");
    localStorage.removeItem("lx_id_token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiry"); // Очищаем expiry
    localStorage.removeItem("lx_session_id"); // Очищаем session
  }

  // Обработка неавторизованных запросов
  handleUnauthorized() {
    console.log("🔒 Unauthorized access - clearing tokens");
    this.clearTokens();

    // Перенаправляем на страницу входа если не на ней
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  // Автоматическое обновление токена
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const sessionId = localStorage.getItem("lx_session_id");
      
      const response = await this.post("/auth/refresh-token", {
        refresh_token: refreshToken,
        session_id: sessionId,
      });

      if (response.access_token || (response.tokens && response.tokens.access_token)) {
        // Обрабатываем оба формата ответа
        const tokens = response.tokens || {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          id_token: response.id_token,
          expires_in: response.expires_in || 3600, // По умолчанию 1 час
        };
        
        this.setTokens(tokens);
        
        // Сохраняем session_id если есть
        if (response.session_id) {
          localStorage.setItem("lx_session_id", response.session_id);
        }
        
        return tokens.access_token;
      } else {
        throw new Error(response.message || "Token refresh failed");
      }
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }
}

// Класс для обработки ошибок API
class APIError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

// Создаем глобальный экземпляр API клиента
export const apiClient = new APIClient();

/**
 * Вызов облачной функции через API Gateway или напрямую
 * @param {string} functionName - имя функции (напр., "user-data", "auth", "sbis-sync")
 * @param {object} data - данные для отправки в функцию
 * @param {object} options - дополнительные опции (headers и т.д.)
 * @returns {Promise<any>} - ответ от функции
 */
export async function callFunction(functionName, data = {}, options = {}) {
  try {
    // В production используем API Gateway: /api/{functionName}
    // В development используем прямые вызовы: http://localhost:3131/{functionName}
    const url = isDevelopment 
      ? `${FUNCTIONS_URL}/${functionName}`
      : `${API_BASE_URL}/${functionName}`;
    
    const response = await apiClient.post(url, data, options);
    return response;
  } catch (error) {
    console.error(`[callFunction] Error calling ${functionName}:`, error);
    throw error;
  }
}

// Утилиты для работы с окружением
export const ENV = {
  isDevelopment,
  isProduction,
  mode: isDevelopment ? "development" : "production",
};

// Конфигурация для отладки
export const DEBUG = {
  enabled: isDevelopment || import.meta.env.VITE_DEBUG === "true",
  apiLogging: isDevelopment,
  verboseErrors: isDevelopment,
};

// Экспорт по умолчанию
export default {
  API_BASE_URL,
  AUTH_API_URL,
  API_ENDPOINTS,
  apiClient,
  ENV,
  DEBUG,
  APIError,
};

// Глобальная конфигурация для совместимости
if (typeof window !== "undefined") {
  window.LX_FBC_CONFIG = {
    API_BASE_URL,
    AUTH_API_URL,
    API_ENDPOINTS,
    ENV,
    DEBUG,
  };
}
